import { useNavigate } from "react-router-dom";
import { Text } from "@mantine/core";
import { BaseContainer } from "../common/BaseContainer";
import { FolderGridItem } from "./FolderGridItem";
import {
    FolderIcon,
    DashboardIcon,
    LineChartIcon,
    GaugeChartIcon,
    PieChartIcon,
    StackedLineChartIcon,
    BarLineChartIcon,
    RadarChartIcon,
    BarChartIcon,
    ReportIcon,
    FormIcon,
    ScoreCardIcon,
    AreaChartIcon,
    CalendarChartIcon,
    MapChartIcon,
    TabGroupIcon,
} from "@bi-studio/shared/components";
import type {
    FolderTreeNode,
    Report,
    Dashboard,
    Chart,
    Form,
    TabGroup,
} from "@bi-studio/shared/lib";

interface RootFolderGridProps {
    topLevelFolders: FolderTreeNode[];
    rootDashboards: Dashboard[];
    rootCharts: Chart[];
    rootReports: Report[];
    rootForms: Form[];
    rootTabGroups: TabGroup[];
    onMove: (type: any, id: string) => void;
    onDelete: (type: any, id: string) => void;
    onViewDetails: (type: any, id: string) => void;
    onEdit?: (type: any, id: string) => void;
    onItemClick: (
        type: "report" | "dashboard" | "chart" | "form" | "tab_group" | "folder",
        id: string
    ) => void;
    emptyStateText?: React.ReactNode;
}

export function RootFolderGrid({
    topLevelFolders,
    rootDashboards,
    rootCharts,
    rootReports,
    rootForms,
    rootTabGroups,
    onMove,
    onDelete,
    onViewDetails,
    onEdit,
    onItemClick,
    emptyStateText = "No content in root folder",
}: RootFolderGridProps) {
    const navigate = useNavigate();

    if (
        topLevelFolders.length === 0 &&
        rootDashboards.length === 0 &&
        rootCharts.length === 0 &&
        rootReports.length === 0 &&
        rootForms.length === 0 &&
        rootTabGroups.length === 0
    ) {
        return (
            <BaseContainer p="sm">
                <div className="flex flex-col items-center justify-center">
                    <Text c="dimmed">{emptyStateText}</Text>
                </div>
            </BaseContainer>
        );
    }

    return (
        <BaseContainer>
            <div className="grid gap-2">
                {/* Folders first */}
                {[...topLevelFolders]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((folder) => (
                        <FolderGridItem
                            key={folder.id}
                            id={folder.id}
                            name={folder.name}
                            type="folder"
                            icon={<FolderIcon size={32} />}
                            onClick={() => navigate(`/folders/${folder.id}`)}
                            onMove={onMove}
                            onDelete={onDelete}
                            onEdit={onEdit}
                            deleteLabel="Delete"
                        />
                    ))}

                {/* Then dashboards, charts, reports - alphabetically sorted */}
                {[
                    ...rootDashboards.map((item) => ({
                        ...item,
                        type: "dashboard" as const,
                        Icon: DashboardIcon,
                        displayName: item.name,
                        displayDescription: item.description,
                    })),
                    ...rootCharts.map((item) => ({
                        ...item,
                        type: "chart" as const,
                        Icon: () => {
                            if (item.type === "line") return <LineChartIcon size={32} />;
                            if (item.type === "gauge") return <GaugeChartIcon size={32} />;
                            if (item.type === "pie") return <PieChartIcon size={32} />;
                            if (item.type === "stacked-line")
                                return <StackedLineChartIcon size={32} />;
                            if (item.type === "stacked-bar-line")
                                return <BarLineChartIcon size={32} />;
                            if (item.type === "radar") return <RadarChartIcon size={32} />;
                            if (item.type === "scorecard") return <ScoreCardIcon size={32} />;
                            if (item.type === "area") return <AreaChartIcon size={32} />;
                            if (item.type === "calendar") return <CalendarChartIcon size={32} />;
                            if (item.type === "map") return <MapChartIcon size={32} />;
                            return <BarChartIcon size={32} />;
                        },
                        displayName: item.config.title || `Chart ${item.id}`,
                        displayDescription: null,
                    })),
                    ...rootReports.map((item) => ({
                        ...item,
                        type: "report" as const,
                        Icon: () => <ReportIcon size={32} />,
                        displayName: item.name,
                        displayDescription: item.description,
                    })),
                    ...rootForms.map((item) => ({
                        ...item,
                        type: "form" as const,
                        Icon: FormIcon,
                        displayName: item.name,
                        displayDescription: item.description,
                    })),
                    ...rootTabGroups.map((item) => ({
                        ...item,
                        type: "tab_group" as const,
                        Icon: TabGroupIcon,
                        displayName: item.name,
                        displayDescription: item.description,
                    })),
                ]
                    .sort((a, b) => a.displayName.localeCompare(b.displayName))
                    .map((item) => {
                        const IconComponent = item.Icon;
                        return (
                            <FolderGridItem
                                key={item.id}
                                id={item.id}
                                name={item.displayName}
                                description={item.displayDescription}
                                type={item.type}
                                icon={<IconComponent />}
                                onClick={() => onItemClick(item.type, item.id)}
                                onMove={onMove}
                                onDelete={onDelete}
                                onViewDetails={onViewDetails}
                                onEdit={onEdit || (['dashboard', 'report', 'chart', 'tab_group', 'form'].includes(item.type) ? (type, id) => {
                                    const pathType = type === 'tab_group' ? 'tab-groups' : `${type}s`;
                                    navigate(`/${pathType}/${id}/edit`);
                                } : undefined)}
                            />
                        );
                    })}
            </div>
        </BaseContainer>
    );
}
